import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { CookieService } from 'ngx-cookie-service';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { environment } from '@env/environment';

import { SessionEndService } from './session-end.service';
import { UserService } from './user.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private userService = inject(UserService);
  private http = inject(HttpClient);
  private cookie = inject(CookieService);
  private sessionEnd = inject(SessionEndService);

  /** The token refresh in flight, shared by every request that fails with 401 while it runs. */
  private refreshInFlight: Observable<string> | null = null;
  private readonly refreshUrl = `${environment.companyApiBaseUrl}/auth/refresh`;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // The refresh call's own failure is handled by startRefresh, which ends the session. Handling it here too would
    // queue it behind the refresh it belongs to and hang both.
    if (request.url === this.refreshUrl) {
      return next.handle(request);
    }

    const authToken = this.userService.getAuthToken();
    if (authToken) {
      request = this.addToken(request, authToken);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401Error(request, next);
        }
        // Every other error, 403 included, goes back to the screen that made the request, which shows its message.
        console.error('HTTP Interceptor Error:', error);
        return throwError(() => error);
      })
    );
  }

  private handle401Error(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Sent with a token a refresh has replaced since: retry it with the current one instead of refreshing again.
    const currentToken = this.userService.getAuthToken();
    if (!this.refreshInFlight && currentToken && req.headers.get('Authorization') !== `Bearer ${currentToken}`) {
      const retry = this.addToken(req, currentToken);
      return next
        .handle(retry)
        .pipe(
          catchError((error: HttpErrorResponse) =>
            error.status === 401 ? this.handle401Error(retry, next) : throwError(() => error)
          )
        );
    }

    const refresh$ = this.refreshInFlight ?? this.startRefresh();
    if (!refresh$) {
      this.sessionEnd.endByUser();
      return throwError(() => new Error('Refresh token is not available'));
    }

    return refresh$.pipe(
      switchMap(token =>
        next.handle(this.addToken(req, token)).pipe(
          catchError((error: HttpErrorResponse) => {
            // The fresh token was refused too: another refresh would only loop, so the session is over.
            if (error.status === 401) {
              this.sessionEnd.endByUser();
            }
            return throwError(() => error);
          })
        )
      )
    );
  }

  /**
   * Starts the one refresh every 401 shares until it settles, or returns null when there is no refresh token. It runs
   * on its own subscription (`shareReplay` without `refCount`), so a screen that unsubscribes cannot abort it for the
   * requests waiting on it, and it stops being shared as soon as it settles, so a later 401 starts a new one.
   */
  private startRefresh(): Observable<string> | null {
    const refreshToken = this.cookie.get('refreshToken');
    if (!refreshToken) {
      return null;
    }

    const settle = () => {
      if (this.refreshInFlight === refresh$) {
        this.refreshInFlight = null;
      }
    };
    const refresh$: Observable<string> = this.http
      .post<{ accessToken: string }>(this.refreshUrl, { refreshToken })
      .pipe(
        map(response => response.accessToken),
        // Settle before the waiters hear the outcome, so a 401 they cause starts a new refresh.
        tap({
          next: token => {
            settle();
            this.setAccessToken(token);
          },
          error: () => {
            settle();
            this.sessionEnd.endByUser();
          }
        }),
        finalize(settle),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    this.refreshInFlight = refresh$;
    return refresh$;
  }

  private setAccessToken(token: string): void {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      // Locally the Company login sets host-only cookies; a browser refuses the shared domain there.
      this.cookie.set('accessToken', token, undefined, '/', undefined, true, 'None');
    } else {
      this.cookie.set('accessToken', token, undefined, '/', `.${environment.domainName}`, true, 'None');
    }
  }

  private addToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }
}
